import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { BaseCardScene } from './base-card-scene';
import { PortalScene, PortalSceneConfig } from './portal-scene';
import { TurnOverIndicator } from '../components/turn-over-indicator';

/**
 * Layout knobs for elements we want to position without hunting through code.
 * Every value should be easy to tweak from the consuming scene config.
 */
export interface VoodooShowcaseLayout {
  titlePosition: THREE.Vector3;      // Where the "Voodoo" title sits relative to the card
  titleScale: number;                // Uniform scale for the text mesh
  logoAnchor: THREE.Vector3;         // Base position for the floating logo
  logoRotation: THREE.Euler;         // Base orientation before the 45 degree tilt animation
  logoScale?: number;                // Optional uniform scale for the floating logo asset
  textPanelPosition: THREE.Vector3;  // Position for the animated portal text
  textPanelScale: THREE.Vector3;     // Allows us to resize the panel without remodeling
}

/**
 * ASCII layout sketch (not to scale):
 *
 *                     ^ y
 *                     |
 *        Floating logo (45°) - hangs off the card edge
 *                __________________________
 *               /                         /|
 *              /                         / |
 *             /_________________________/  |
 *             |                         |  |
 *             |    Title: "Voodoo"     |  |
 *             |   snuggled near portal  |  |
 *             | "Keep this card..."   |  /
 *             |_________________________| /
 *             |_________________________|/
 *
 *   - Card plane lies flat on X/Z, camera looking down -Y (MindAR default).
 *   - Title now hugs the portal opening so it feels integrated with the copy.
 *   - Logo floats farther out, still tilted 45° to feel detached and premium.
 *   - Typewriter text sits over portal mouth so copy feels integrated.
 */

/**
 * Animation knobs that we will wire up later once actual behavior is needed.
 */
export interface VoodooShowcaseAnimationConfig {
  // Portal opening animation (happens first)
  portalStartDelay: number;      // Delay before portal starts opening
  portalDotDuration: number;     // Duration of initial dot phase
  portalLineDuration: number;    // Duration of line expansion (width grows)
  portalOpenDuration: number;    // Duration of final opening (height grows)

  // Element animations (after portal opens)
  logoStartDelay: number;     // Delay before logo fades in (1st element)
  logoFadeDuration: number;   // Seconds to fade the logo in
  titleStartDelay: number;    // Delay before title fades in (2nd element)
  titleFadeDuration: number;  // Seconds to fade the title in
  typewriterStartDelay: number; // Delay before text starts animating (3rd element)
  typewriterCharDelay: number; // Delay between each character for the typewriter effect
  logoFloatAmplitude: number; // Height delta for floating logo bob
  logoFloatSpeed: number;     // Speed multiplier for bobbing animation
  showTitle?: boolean;        // Toggle to show/hide title (default: true)
  showTypewriter?: boolean;   // Toggle to show/hide typewriter text (default: true)
  showLogo?: boolean;         // Toggle to show/hide floating logo (default: true)
}

/**
 * Public configuration for scene consumers (index.ts, standalone, etc.)
 */
export interface VoodooShowcaseSceneConfig {
  portal?: PortalSceneConfig;         // Optional overrides passed to the internal portal scene
  layout: VoodooShowcaseLayout;       // Positioning knobs mentioned above
  animation: VoodooShowcaseAnimationConfig; // Animation knobs for fade/typewriter/float
  titleText?: string;                 // Event title copy, defaults to "Voodoo"
  titleFont?: string;                 // CSS font family for title (default: "Amatic SC")
  typewriterFont?: string;            // CSS font family for typewriter text (default: "Amatic SC")
  floatingLogoUrl: string;            // GLB path (voodoo-logo.glb) - mesh already has internal animation
  typewriterCopy: string;             // Portal overlay copy (Keep this card...)
  sceneId?: string;                   // Optional custom scene ID (default: 'voodoo-showcase')
  name?: string;                      // Optional custom scene name (default: 'Voodoo Showcase')
  description?: string;               // Optional custom scene description
}

/**
 * Scene that builds on the portal effect and adds title/logo/typewriter beats.
 */
export class VoodooShowcaseScene extends BaseCardScene {
  private readonly config: VoodooShowcaseSceneConfig;
  private readonly portalScene: PortalScene;
  private readonly loader: GLTFLoader = new GLTFLoader();

  private titleGroup: THREE.Group | null = null;
  private titleMaterial: THREE.MeshBasicMaterial | null = null;
  private titleTexture: THREE.CanvasTexture | null = null;

  private logoRoot: THREE.Group | null = null;
  private logoBasePosition: THREE.Vector3 = new THREE.Vector3();
  private logoMixer: THREE.AnimationMixer | null = null;
  private logoScene: THREE.Group | null = null;

  private typewriterMesh: THREE.Mesh | null = null;
  private typewriterMaterial: THREE.MeshBasicMaterial | null = null;
  private typewriterCanvas: HTMLCanvasElement | null = null;
  private typewriterCtx: CanvasRenderingContext2D | null = null;
  private typewriterTexture: THREE.CanvasTexture | null = null;
  private typewriterVisibleChars = 0;
  private typewriterTotalChars = 0;
  private typewriterScrollOffset = 0;
  private typewriterMaxScroll = 0;

  private readonly titleText: string;
  private readonly typewriterText: string;
  private readonly titleFont: string;
  private readonly typewriterFont: string;
  private isSceneVisible = true;
  private turnOverIndicator: TurnOverIndicator | null = null;

  constructor(config: VoodooShowcaseSceneConfig) {
    super(
      {
        id: config.sceneId ?? 'voodoo-showcase',
        name: config.name ?? 'Voodoo Showcase',
        description: config.description ?? 'Detached hero view built on the portal scene foundation'
      },
      {
        cardWidth: config.portal?.cardWidth,
        cardHeight: config.portal?.cardHeight,
        defaultScale: config.portal?.defaultScale
        // NOTE: Do NOT apply defaultRotation to the scene root
        // Only the inner portal should be rotated
        // Text elements need to remain camera-facing
        // But DO apply defaultScale so everything scales uniformly
      }
    );
    this.config = config;
    this.titleText = config.titleText ?? 'Voodoo';
    this.typewriterText = config.typewriterCopy;
    this.titleFont = config.titleFont ?? '"Amatic SC", sans-serif';
    this.typewriterFont = config.typewriterFont ?? '"Amatic SC", sans-serif';

    // Prepare portal scene so we can reuse its geometry + clipping pipeline
    this.portalScene = new PortalScene({
      ...config.portal,
      defaultRotation: config.portal?.defaultRotation ?? new THREE.Euler(Math.PI / 2, 0, 0)
    });
  }

  init(parent: THREE.Object3D, renderer: THREE.WebGLRenderer): void {
    if (this.initialized) {
      console.warn('VoodooShowcaseScene already initialized');
      return;
    }

    this.renderer = renderer;
    parent.add(this.root);

    // Build portal/tunnel first so our overlay elements sit above it
    this.portalScene.init(this.root, renderer);

    // Set initial portal scale to a tiny dot (will animate to full size)
    const portalRoot = this.portalScene.getRoot();
    if (portalRoot) {
      portalRoot.scale.set(0.01, 1, 0.01);  // Start as a tiny dot
    }

    // Only create text layers if enabled
    if (this.config.animation.showTitle !== false) {
      this.createTitle();
    }
    if (this.config.animation.showLogo !== false) {
      this.loadLogo();
    }
    if (this.config.animation.showTypewriter !== false) {
      this.createTypewriterPanel();
    }

    this.createTurnOverIndicator();
    this.setupLighting();

    this.initialized = true;
  }

  update(delta: number): void {
    if (!this.initialized || !this.isSceneVisible) return;

    this.elapsedTime += delta;

    this.portalScene.update(delta);
    this.updatePortalOpening();
    this.updateLogoFade();
    this.updateLogoFloat(delta);
    this.updateTitleFade();
    this.updateTypewriter();
  }

  setVisible(visible: boolean): void {
    const becameVisible = visible && !this.isSceneVisible;
    this.isSceneVisible = visible;
    super.setVisible(visible);

    if (this.portalScene.getRoot()) {
      this.portalScene.setVisible(visible);
    }

    if (becameVisible) {
      this.resetTimedAnimations();
    }
  }

  dispose(): void {
    this.portalScene.dispose();

    if (this.titleGroup) {
      this.root.remove(this.titleGroup);
      this.titleGroup = null;
    }
    this.titleMaterial = null;
    this.titleTexture = null;

    if (this.logoRoot) {
      this.root.remove(this.logoRoot);
      this.logoRoot = null;
    }
    if (this.logoMixer) {
      this.logoMixer.stopAllAction();
      this.logoMixer = null;
    }

    if (this.typewriterMesh) {
      this.root.remove(this.typewriterMesh);
      this.typewriterMesh = null;
    }
    this.typewriterMaterial = null;
    this.typewriterTexture = null;
    this.typewriterCanvas = null;
    this.typewriterCtx = null;

    if (this.turnOverIndicator) {
      this.turnOverIndicator.dispose();
      this.turnOverIndicator = null;
    }

    this.typewriterVisibleChars = 0;

    // Base class dispose will handle tracked textures, materials, and geometries
    super.dispose();
  }

  private createTitle(): void {
    const texture = this.generateTitleTexture(this.titleText);
    this.titleTexture = texture;
    this.titleMaterial = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      opacity: 0
    });

    const plane = new THREE.PlaneGeometry(1, 0.35);
    const mesh = new THREE.Mesh(plane, this.titleMaterial);
    mesh.renderOrder = 2;

    // Track resources for automatic disposal
    this.trackTexture(texture);
    this.trackMaterial(this.titleMaterial);
    this.trackGeometry(plane);

    this.titleGroup = new THREE.Group();
    this.titleGroup.position.copy(this.config.layout.titlePosition);
    this.titleGroup.scale.setScalar(this.config.layout.titleScale);
    this.titleGroup.add(mesh);
    this.root.add(this.titleGroup);
  }

  private resetTimedAnimations(): void {
    this.resetElapsedTime(); // Use base class method
    this.typewriterVisibleChars = 0;
    this.typewriterScrollOffset = 0;
    this.typewriterMaxScroll = 0;
    if (this.titleMaterial) {
      this.titleMaterial.opacity = 0;
    }
    this.drawTypewriterText('');

    // Reset portal to starting scale (tiny dot)
    const portalRoot = this.portalScene.getRoot();
    if (portalRoot) {
      portalRoot.scale.set(0.01, 1, 0.01);
    }
  }

  private generateTitleTexture(text: string): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to create canvas for title texture');

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Match the headline style from voodoo.css (Amatic SC, bold, white)
    ctx.font = `bold 220px ${this.titleFont}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 8;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.45)';
    ctx.strokeText(text, canvas.width / 2, canvas.height / 2);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.encoding = THREE.sRGBEncoding;
    texture.needsUpdate = true;
    return texture;
  }

  private loadLogo(): void {
    this.loader.load(
      this.config.floatingLogoUrl,
      (gltf) => {
        this.logoScene = gltf.scene;
        const scale = this.config.layout.logoScale ?? 1;
        this.logoScene.scale.setScalar(scale);

        // Start invisible for fade-in effect
        this.logoScene.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            mesh.castShadow = true;
            mesh.receiveShadow = true;
            // Make materials transparent for fade-in
            if (mesh.material) {
              const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
              materials.forEach(mat => {
                mat.transparent = true;
                mat.opacity = 0;
              });
            }
          }
        });

        this.logoRoot = new THREE.Group();
        this.logoRoot.position.copy(this.config.layout.logoAnchor);
        // Honor only the configured rotation so we can keep a single-axis tilt when desired
        this.logoRoot.rotation.copy(this.config.layout.logoRotation);
        this.logoBasePosition.copy(this.config.layout.logoAnchor);

        this.logoRoot.add(this.logoScene);
        this.root.add(this.logoRoot);

        if (gltf.animations && gltf.animations.length > 0) {
          this.logoMixer = new THREE.AnimationMixer(this.logoScene);
          gltf.animations.forEach((clip) => {
            const action = this.logoMixer!.clipAction(clip);
            action.play();
          });
        }
      },
      undefined,
      (error) => {
        console.error('Failed to load voodoo logo', error);
      }
    );
  }

  private createTypewriterPanel(): void {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;  // Doubled height for more text space
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to create canvas for typewriter text');

    this.typewriterCanvas = canvas;
    this.typewriterCtx = ctx;
    this.typewriterTotalChars = this.typewriterText.length;

    const texture = new THREE.CanvasTexture(canvas);
    texture.encoding = THREE.sRGBEncoding;
    texture.needsUpdate = true;
    this.typewriterTexture = texture;

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false
    });
    this.typewriterMaterial = material;

    const plane = new THREE.PlaneGeometry(1, 0.5);  // Doubled height to match canvas
    const mesh = new THREE.Mesh(plane, material);
    mesh.position.copy(this.config.layout.textPanelPosition);
    mesh.scale.copy(this.config.layout.textPanelScale);
    mesh.renderOrder = 2;

    // Track resources for automatic disposal
    this.trackTexture(texture);
    this.trackMaterial(material);
    this.trackGeometry(plane);

    this.typewriterMesh = mesh;
    this.root.add(mesh);

    // Draw empty state so the panel background is visible before animation starts
    this.drawTypewriterText('');
  }

  private drawTypewriterText(text: string): void {
    if (!this.typewriterCanvas || !this.typewriterCtx || !this.typewriterTexture) return;

    const ctx = this.typewriterCtx;
    const canvas = this.typewriterCanvas;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.font = `bold 70px ${this.typewriterFont}`;
    ctx.fillStyle = '#fef6e4';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const padding = 80;
    const maxLineWidth = canvas.width - padding * 2;
    const lines = this.wrapText(text, maxLineWidth, ctx);
    const lineHeight = 80;
    const totalHeight = lineHeight * lines.length;

    // Calculate visible area and overflow
    const visibleHeight = canvas.height - padding;
    const overflow = Math.max(0, totalHeight - visibleHeight);
    this.typewriterMaxScroll = overflow;

    // Apply scroll offset when there's overflow
    let offsetY = padding / 2 + lineHeight / 2 - this.typewriterScrollOffset;

    lines.forEach((line) => {
      // Only draw lines that are visible in the viewport
      if (offsetY > -lineHeight && offsetY < canvas.height + lineHeight) {
        ctx.fillText(line, canvas.width / 2, offsetY);
      }
      offsetY += lineHeight;
    });

    this.typewriterTexture.needsUpdate = true;
  }

  private wrapText(text: string, maxWidth: number, ctx: CanvasRenderingContext2D): string[] {
    if (!text) return [];

    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const { width } = ctx.measureText(testLine);
      if (width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  }

  private updateLogoFade(): void {
    if (!this.logoScene) return;
    const { logoStartDelay, logoFadeDuration } = this.config.animation;
    if (this.elapsedTime < logoStartDelay) return;

    const t = this.elapsedTime - logoStartDelay;
    const progress = Math.min(1, t / logoFadeDuration);

    this.logoScene.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        if (mesh.material) {
          const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          materials.forEach(mat => {
            mat.opacity = progress;
          });
        }
      }
    });
  }

  private updateTitleFade(): void {
    if (!this.titleMaterial) return;
    const { titleStartDelay, titleFadeDuration } = this.config.animation;
    if (this.elapsedTime < titleStartDelay) return;

    const t = this.elapsedTime - titleStartDelay;
    const progress = Math.min(1, t / titleFadeDuration);
    this.titleMaterial.opacity = progress;
  }

  private updateLogoFloat(delta: number): void {
    if (this.logoMixer) {
      this.logoMixer.update(delta);
    }
    if (!this.logoRoot) return;

    const { logoFloatAmplitude, logoFloatSpeed } = this.config.animation;
    const speed = logoFloatSpeed || 1;
    const amplitude = logoFloatAmplitude || 0;
    const offset = Math.sin(this.elapsedTime * speed) * amplitude;
    this.logoRoot.position.y = this.logoBasePosition.y + offset;
  }

  private updateTypewriter(): void {
    if (!this.typewriterText || !this.typewriterTexture) return;

    const { typewriterStartDelay, typewriterCharDelay } = this.config.animation;
    if (this.elapsedTime < typewriterStartDelay) return;

    const delay = Math.max(typewriterCharDelay, 0.01);
    const elapsed = this.elapsedTime - typewriterStartDelay;
    const charsToShow = Math.min(this.typewriterTotalChars, Math.floor(elapsed / delay));

    // Update visible characters
    if (charsToShow > this.typewriterVisibleChars) {
      this.typewriterVisibleChars = charsToShow;
      const visibleText = this.typewriterText.substring(0, charsToShow);
      this.drawTypewriterText(visibleText);
    }

    // Smooth scroll up if there's overflow (after all text is visible)
    if (this.typewriterVisibleChars >= this.typewriterTotalChars && this.typewriterMaxScroll > 0) {
      const scrollSpeed = 20; // pixels per second
      const scrollProgress = (elapsed - this.typewriterTotalChars * delay) * scrollSpeed;
      this.typewriterScrollOffset = Math.min(scrollProgress, this.typewriterMaxScroll);

      // Redraw to show scrolled position
      if (scrollProgress <= this.typewriterMaxScroll) {
        const visibleText = this.typewriterText.substring(0, this.typewriterVisibleChars);
        this.drawTypewriterText(visibleText);
      }
    }
  }

  /**
   * Animate portal opening in three stages:
   * 1. Dot: tiny point at center
   * 2. Line: expand width to card width
   * 3. Open: expand height to card height
   */
  private updatePortalOpening(): void {
    const portalRoot = this.portalScene.getRoot();
    if (!portalRoot) return;

    const {
      portalStartDelay,
      portalDotDuration,
      portalLineDuration,
      portalOpenDuration
    } = this.config.animation;

    // Don't start until delay has passed
    if (this.elapsedTime < portalStartDelay) return;

    const elapsed = this.elapsedTime - portalStartDelay;

    // Phase 1: Dot (stays at 0.01 scale)
    const dotEnd = portalDotDuration;

    // Phase 2: Line (expand width from 0.01 to 1.0)
    const lineStart = dotEnd;
    const lineEnd = lineStart + portalLineDuration;

    // Phase 3: Open (expand height from 0.01 to 1.0)
    const openStart = lineEnd;
    const openEnd = openStart + portalOpenDuration;

    if (elapsed < dotEnd) {
      // Phase 1: Dot - stay tiny
      portalRoot.scale.set(0.01, 1, 0.01);
    } else if (elapsed < lineEnd) {
      // Phase 2: Line - expand width
      const lineProgress = (elapsed - lineStart) / portalLineDuration;
      const smoothProgress = this.easeInOutCubic(lineProgress);
      const scaleX = 0.01 + smoothProgress * (1.0 - 0.01);
      portalRoot.scale.set(scaleX, 1, 0.01);
    } else if (elapsed < openEnd) {
      // Phase 3: Open - expand height
      const openProgress = (elapsed - openStart) / portalOpenDuration;
      const smoothProgress = this.easeInOutCubic(openProgress);
      const scaleZ = 0.01 + smoothProgress * (1.0 - 0.01);
      portalRoot.scale.set(1.0, 1, scaleZ);
    } else {
      // Animation complete - fully open
      portalRoot.scale.set(1.0, 1, 1.0);
    }
  }

  /**
   * Easing function for smooth animation
   */
  private easeInOutCubic(t: number): number {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  private createTurnOverIndicator(): void {
    this.turnOverIndicator = new TurnOverIndicator({
      cardWidth: this.cardWidth,
      cardHeight: this.cardHeight,
      text: 'Turn over for more',
      color: '#00ff00',
      offsetFromBottom: 0.4,
      heightAboveCard: 0.02
    });

    this.root.add(this.turnOverIndicator.getGroup());
  }

  private setupLighting(): void {
    this.addAmbientLight(0xffffff, 0.85);
    const key = this.addDirectionalLight(0xffffff, 0.6, new THREE.Vector3(0.6, 1.2, 0.4));
    key.castShadow = false;
    const rim = this.addDirectionalLight(0x8fd3ff, 0.4, new THREE.Vector3(-0.5, 1, -0.5));
    rim.castShadow = false;
  }
}
